/**
 * OnboardingPage (Scout by EQ)
 *
 * Tensol-style guided onboarding to connect core tools.
 * MVP: VIP Gmail (read-only) connect + status + inbox preview, then continue into app.
 */

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { CheckCircle, Loader2, Mail, Inbox, ArrowRight } from "lucide-react";
import api from "../../utils/axios";

const BRAND = {
  purple: "#5a20ff",
  teal: "#52dcb3",
  bgOffWhite: "#f8f8f6",
};

function formatDate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString();
}

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth0();

  const [vipStatus, setVipStatus] = useState({
    loading: true,
    connected: false,
    tenantId: null,
    connection: null,
    error: null,
  });
  const [threads, setThreads] = useState([]);
  const [threadsLoading, setThreadsLoading] = useState(false);

  const returnUrl = useMemo(() => "/app/onboarding", []);

  const loadVipStatus = async () => {
    setVipStatus((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const { data } = await api.get("/api/vip/gmail/status");
      setVipStatus({
        loading: false,
        connected: !!data.connected,
        tenantId: data.tenantId,
        connection: data.connection,
        error: null,
      });
    } catch (err) {
      setVipStatus({
        loading: false,
        connected: false,
        tenantId: null,
        connection: null,
        error: err?.response?.data?.error || err.message || String(err),
      });
    }
  };

  const loadThreads = async () => {
    setThreadsLoading(true);
    try {
      const { data } = await api.get("/api/vip/gmail/threads", { params: { limit: 5 } });
      setThreads(data.threads || []);
    } catch (err) {
      setThreads([]);
    } finally {
      setThreadsLoading(false);
    }
  };

  useEffect(() => {
    if (!isAuthenticated || isLoading) return;
    loadVipStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isLoading]);

  useEffect(() => {
    if (vipStatus.connected) {
      loadThreads();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vipStatus.connected]);

  const handleVipConnect = async () => {
    try {
      const { data } = await api.get("/api/vip/google/connect", {
        params: { returnUrl },
      });
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      setVipStatus((prev) => ({
        ...prev,
        error: err?.response?.data?.error || err.message || String(err),
      }));
    }
  };

  const finish = () => {
    try {
      localStorage.setItem("onboarding.completed", "1");
      localStorage.setItem("onboarding.completedAt", new Date().toISOString());
    } catch (e) {
      // ignore
    }
    navigate("/app", { replace: true });
  };

  return (
    <div className="min-h-screen" style={{ background: BRAND.bgOffWhite }}>
      <div className="max-w-3xl mx-auto px-4 py-10">
        <div className="bg-white/95 backdrop-blur rounded-2xl shadow-xl border border-black/5 p-6 md:p-10">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h1
                className="text-2xl md:text-3xl font-bold text-black"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Command Centre Onboarding
              </h1>
              <p className="text-black/60 mt-2">
                Setup takes ~3 minutes. Connect the essentials, then you’ll land in the Command Centre.
              </p>
            </div>
            <button
              className="text-sm font-medium text-black/50 hover:text-black"
              onClick={() => navigate("/app/settings?tab=integrations")}
            >
              Open Settings
            </button>
          </div>

          <div className="mt-8 space-y-6">
            {/* Step: VIP Gmail Read-only */}
            <div className="rounded-2xl border border-black/5 bg-[#f8f8f6] p-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <Mail className="w-5 h-5" style={{ color: BRAND.purple }} />
                    <h2 className="text-lg font-semibold text-black" style={{ fontFamily: "'Playfair Display', serif" }}>
                      Connect Gmail (Read-only)
                    </h2>
                  </div>
                  <p className="text-sm text-black/60 mt-1">
                    Grants <span className="font-semibold">gmail.readonly</span> only. EQ can’t send or modify your inbox.
                  </p>
                </div>

                {vipStatus.connected ? (
                  <div className="flex items-center gap-2 text-emerald-700 text-sm font-semibold">
                    <CheckCircle className="w-5 h-5" />
                    Connected
                  </div>
                ) : null}
              </div>

              <div className="mt-4">
                {vipStatus.loading ? (
                  <div className="flex items-center gap-2 text-sm text-black/60">
                    <Loader2 className="w-4 h-4 animate-spin" style={{ color: BRAND.purple }} />
                    Checking connection…
                  </div>
                ) : vipStatus.connected ? (
                  <div className="space-y-3">
                    <div className="text-sm text-black/70">
                      <div>
                        <span className="text-black/40">Account:</span> {vipStatus.connection?.email || "(unknown)"}
                      </div>
                      <div>
                        <span className="text-black/40">Last synced:</span> {formatDate(vipStatus.connection?.updatedAt)}
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-black/70">
                        <Inbox className="w-4 h-4" />
                        Recent inbox sample
                      </div>
                      <button
                        className="text-sm font-medium hover:opacity-90"
                        style={{ color: BRAND.purple }}
                        onClick={loadThreads}
                      >
                        Refresh
                      </button>
                    </div>

                    {threadsLoading ? (
                      <div className="flex items-center gap-2 text-sm text-black/60">
                        <Loader2 className="w-4 h-4 animate-spin" style={{ color: BRAND.purple }} />
                        Loading…
                      </div>
                    ) : threads?.length ? (
                      <ul className="space-y-2">
                        {threads.map((t) => (
                          <li key={t.id} className="p-3 rounded-xl bg-white border border-black/5">
                            <div className="flex items-center justify-between gap-3">
                              <div className="font-semibold text-sm text-black line-clamp-1">
                                {t.subject || "(no subject)"}
                              </div>
                              <div className="text-xs text-black/40 whitespace-nowrap">{formatDate(t.date)}</div>
                            </div>
                            <div className="text-xs text-black/60 mt-1 line-clamp-1">{t.from || ""}</div>
                            <div className="text-xs text-black/40 mt-1 line-clamp-2">{t.snippet || ""}</div>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="text-sm text-black/50">No preview yet.</div>
                    )}

                    {vipStatus.error ? (
                      <div className="text-xs text-red-600">{vipStatus.error}</div>
                    ) : null}
                  </div>
                ) : (
                  <div className="space-y-3">
                    <button
                      onClick={handleVipConnect}
                      className="w-full px-6 py-3 rounded-xl font-semibold text-white transition-all"
                      style={{
                        background: `linear-gradient(90deg, ${BRAND.teal}, ${BRAND.purple})`,
                      }}
                    >
                      Connect Gmail (Read-only)
                    </button>
                    {vipStatus.error ? (
                      <div className="text-xs text-red-600">{vipStatus.error}</div>
                    ) : null}
                  </div>
                )}
              </div>
            </div>

            {/* Finish */}
            <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
              <div className="text-sm text-black/50">You can change these anytime in Settings.</div>
              <button
                onClick={finish}
                disabled={!vipStatus.connected}
                className="px-6 py-3 rounded-xl font-semibold text-white disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                style={{ background: BRAND.purple }}
              >
                Continue to Command Centre
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>

            {!vipStatus.connected ? (
              <div className="text-xs text-black/40">
                If you’re blocked on OAuth, click “Open Settings” and try from Integrations.
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
